import React, { useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import {
	Button,
	Callout,
	Card,
	FormGroup,
	Icon,
	InputGroup,
	Intent,
	NonIdealState,
	Spinner,
	Tag,
} from "@blueprintjs/core";
import { IStore } from "redux/store";
import { commonUtils } from "redux/_common";
import authConstants from "redux/app/auth/constants";

type OwnProps = {};
type DocSyncCheckProps = PropsFromRedux & OwnProps;

type SyncCheckResponse = {
	docId: string;
	vesselId?: string;
	imo?: string;
	vesselDbName?: string;
	wayship: { exists: boolean; rev?: string };
	vesselDb: { exists: boolean; rev?: string };
	inSync: boolean;
	message?: string;
};

function DocSyncCheck(props: DocSyncCheckProps) {
	const [docId, setDocId] = useState("");
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [data, setData] = useState<SyncCheckResponse | null>(null);

	const handleCheck = async () => {
		const trimmed = docId.trim();
		if (!trimmed) {
			setErrorMessage("Document ID is required.");
			setData(null);
			return;
		}

		setLoading(true);
		setErrorMessage(null);
		setData(null);

		try {
			const resp = await fetch(
				`${commonUtils.getAPIBaseUrl()}/api/doc-sync-check?docId=${encodeURIComponent(
					trimmed
				)}`,
				{
					headers: {
						[authConstants.headers.TOKEN]: props.authToken || "",
					},
				}
			);
			const json = (await resp.json()) as SyncCheckResponse | { message?: string };
			if (!resp.ok) {
				setErrorMessage(
					(json as any)?.message || "Failed to check document sync"
				);
				return;
			}
			setData(json as SyncCheckResponse);
		} catch (e: any) {
			setErrorMessage(e?.message || "Network error");
		} finally {
			setLoading(false);
		}
	};

	const renderResult = () => {
		if (loading) {
			return (
				<div className="py-10 flex justify-center">
					<Spinner size={36} />
				</div>
			);
		}

		if (errorMessage) return null;

		if (!data) {
			return (
				<NonIdealState
					icon="search"
					title="No result yet"
					description="Enter a document ID and run the check."
					className="py-10 bg-white rounded-lg shadow-sm border border-gray-100"
				/>
			);
		}

		const syncIntent = data.inSync ? Intent.SUCCESS : Intent.DANGER;

		return (
			<Card elevation={1} className="bg-white">
				<div className="p-2 space-y-4">
					<div className="flex items-center gap-3">
						<Tag intent={syncIntent} large>
							{data.inSync ? "In Sync" : "Not In Sync"}
						</Tag>
						<span className="text-sm text-gray-600">
							Doc ID: <span className="font-mono">{data.docId}</span>
						</span>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div className="rounded-lg border border-gray-100 p-4">
							<h3 className="text-sm font-semibold text-gray-700 mb-2">
								Wayship DB
							</h3>
							<div className="text-sm text-gray-600">
								<div>Exists: {data.wayship.exists ? "Yes" : "No"}</div>
								<div>
									Rev:{" "}
									<span className="font-mono">
										{data.wayship.rev || "—"}
									</span>
								</div>
							</div>
						</div>
						<div className="rounded-lg border border-gray-100 p-4">
							<h3 className="text-sm font-semibold text-gray-700 mb-2">
								Vessel DB
							</h3>
							<div className="text-sm text-gray-600">
								<div>DB: {data.vesselDbName || "—"}</div>
								<div>Exists: {data.vesselDb.exists ? "Yes" : "No"}</div>
								<div>
									Rev:{" "}
									<span className="font-mono">
										{data.vesselDb.rev || "—"}
									</span>
								</div>
							</div>
						</div>
					</div>

					<div className="text-sm text-gray-600">
						<div>
							Vessel ID:{" "}
							<span className="font-mono">{data.vesselId || "—"}</span>
						</div>
						<div>
							IMO: <span className="font-mono">{data.imo || "—"}</span>
						</div>
					</div>

					{data.message && (
						<Callout intent={Intent.WARNING} icon="warning-sign">
							{data.message}
						</Callout>
					)}
				</div>
			</Card>
		);
	};

	return (
		<div className="min-h-[calc(100vh-98px)] bg-gray-50 p-6 md:p-8 lg:p-12">
			<div className="max-w-[1200px] mx-auto space-y-6">
				<div>
					<h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
						<Icon icon="comparison" size={28} className="text-blue-600" />
						Doc Sync Check
					</h1>
					<p className="text-gray-600">
						Compare document revisions between the Wayship DB and the vessel DB.
					</p>
				</div>

				{errorMessage && (
					<Callout intent={Intent.DANGER} icon="error" title="Error">
						{errorMessage}
					</Callout>
				)}

				<Card elevation={1} className="bg-white">
					<div className="p-2">
						<FormGroup label="Document ID" labelInfo="(required)" className="mb-0">
							<div className="flex gap-3 flex-wrap items-end">
								<InputGroup
									placeholder="Enter document ID..."
									value={docId}
									disabled={loading}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setDocId(e.target.value)
									}
									onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
										if (e.key === "Enter") {
											handleCheck();
										}
									}}
									className="flex-1 min-w-[280px]"
								/>
								<Button
									intent={Intent.PRIMARY}
									icon="search"
									loading={loading}
									disabled={!docId.trim() || loading}
									onClick={handleCheck}
								>
									Check Sync
								</Button>
							</div>
						</FormGroup>
					</div>
				</Card>

				{renderResult()}
			</div>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {
		authToken: store.app.auth.authToken,
	};
}

const reduxConnector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(DocSyncCheck);

